import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setCredentials } from "../store/slices/authSlice";
import { useLoginMutation, useLoginAdminViaTelegramMutation, useVerify2FAMutation } from "../api";
import { LanguageSelectorPublic } from "./LanguageSelectorPublic";
import Cookies from "js-cookie";
import ApiError from "./ui/ApiError";
import { useTelegramWebApp } from "../composables/useTelegramWebApp";

const SignInPage: React.FC = () => {
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [searchParams] = useSearchParams();
    const [accessDeniedError, setAccessDeniedError] = useState("");
    const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
    const [isAutoLoginAttempted, setIsAutoLoginAttempted] = useState(false);
    const [is2FARequired, setIs2FARequired] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [tempUserId, setTempUserId] = useState<number | null>(null);
    const [verificationError, setVerificationError] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation(["common", "auth"]);
    const [loginMutation, { isLoading, error }] = useLoginMutation();
    const [loginAdminViaTelegram, { isLoading: isTelegramLoggingIn, error: telegramLoginError }] =
        useLoginAdminViaTelegramMutation();
    const [verify2FA, { isLoading: isVerifying, error: verifyError }] = useVerify2FAMutation();
    const { isTelegramWebApp, telegramId } = useTelegramWebApp();

    // Проверяем параметр error в URL
    useEffect(() => {
        const errorParam = searchParams.get("error");
        if (errorParam === "access_denied") {
            setAccessDeniedError(
                "Доступ запрещен. Только администраторы могут войти в систему."
            );
        }
    }, [searchParams]);

    // Автоматический вход через Telegram WebApp
    useEffect(() => {
        const tryAutoLoginViaTelegram = async () => {
            if (!isTelegramWebApp || isAutoLoginAttempted || !telegramId) {
                return;
            }

            setIsAutoLoginAttempted(true);
            setIsAutoLoggingIn(true);

            try {
                const result = await loginAdminViaTelegram(telegramId).unwrap();

                // Сохраняем токен в куки
                Cookies.set("token", result.token!, { expires: 7 });

                // Кэшируем роли пользователя в localStorage
                if (result.user?.roles && result.user.roles.length > 0) {
                    localStorage.setItem(
                        "userRoles",
                        JSON.stringify(result.user.roles)
                    );
                }

                // Кэшируем язык пользователя в localStorage
                if ((result.user as any)?.language) {
                    localStorage.setItem(
                        "userLanguage",
                        (result.user as any).language
                    );
                }

                // Обновляем Redux store
                dispatch(
                    setCredentials({
                        user: result.user as any,
                        token: result.token ?? null,
                    })
                );

                // Перенаправляем на dashboard
                navigate("/dashboard");
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("Admin Telegram auto-login error", err);
                setIsAutoLoggingIn(false);
            }
        };

        tryAutoLoginViaTelegram();
    }, [
        isTelegramWebApp,
        telegramId,
        isAutoLoginAttempted,
        loginAdminViaTelegram,
        dispatch,
        navigate,
    ]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const result = await loginMutation({
                login: login,
                password: password,
                source: "admin",
            }).unwrap();

            if (result["2fa_required"]) {
                setIs2FARequired(true);
                setTempUserId(result.user_id || null);
                return;
            }

            const token = result.token!;

            // Кэшируем роли пользователя в localStorage
            if (result.user?.roles && result.user.roles.length > 0) {
                localStorage.setItem(
                    "userRoles",
                    JSON.stringify(result.user.roles)
                );
            }

            // Кэшируем язык пользователя в localStorage
            if ((result.user as any)?.language) {
                localStorage.setItem(
                    "userLanguage",
                    (result.user as any).language
                );
            }

            // Обновляем Redux store
            dispatch(
                setCredentials({
                    user: result.user as any,
                    token: token,
                })
            );

            // Перенаправляем на dashboard
            navigate("/dashboard");
        } catch (err) {
            console.log(err);
            // Ошибка обрабатывается автоматически RTK Query
        }
    };

    const handle2FASubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setVerificationError("");

        if (!tempUserId) return;

        try {
            const result = await verify2FA({
                user_id: tempUserId,
                code: verificationCode,
            }).unwrap();

            const token = result.token!;

            // Сохраняем токен в куки
            Cookies.set("token", token, { expires: 7 });

            // Кэшируем роли пользователя в localStorage
            if (result.user?.roles && result.user.roles.length > 0) {
                localStorage.setItem(
                    "userRoles",
                    JSON.stringify(result.user.roles)
                );
            }

            // Кэшируем язык пользователя в localStorage
            if ((result.user as any)?.language) {
                localStorage.setItem(
                    "userLanguage",
                    (result.user as any).language
                );
            }

            // Обновляем Redux store
            dispatch(
                setCredentials({
                    user: result.user as any,
                    token: token,
                })
            );

            // Перенаправляем на dashboard
            navigate("/dashboard");
        } catch (err: any) {
            console.error("2FA verification error", err);
            if (err.data?.message) {
                setVerificationError(err.data.message);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
            {/* Селектор языка в правом верхнем углу */}
            <div className="absolute top-4 right-4">
                <LanguageSelectorPublic />
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <img
                    className="mx-auto h-12 w-auto"
                    src="/alny-logo.png"
                    alt="Alny Logo"
                />
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-200">
                    {t("auth.signin")}
                </h2>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    {isAutoLoggingIn && (
                        <div className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                            Проверка авторизации через Telegram...
                        </div>
                    )}
                    {!is2FARequired ? (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            {accessDeniedError && (
                                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                                {accessDeniedError}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <ApiError
                                    error={error}
                                    fallbackMessage={t("auth.login_error")}
                                />
                            )}

                            <div>
                                <label
                                    htmlFor="login"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    {t("auth.email_or_phone")}
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="login"
                                        name="login"
                                        type="text"
                                        autoComplete="username"
                                        required
                                        value={login}
                                        onChange={(e) => setLogin(e.target.value)}
                                        placeholder={t(
                                            "auth.email_or_phone_placeholder"
                                        )}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    {t("auth.password")}
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {isLoading
                                        ? t("auth.logging_in")
                                        : t("auth.login_button")}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form className="space-y-6" onSubmit={handle2FASubmit}>
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    Двухфакторная аутентификация
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Введите 6-значный код, отправленный в ваш Telegram
                                </p>
                            </div>

                            {verificationError && (
                                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                                {verificationError}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label
                                    htmlFor="2fa-code"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Код верификации
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="2fa-code"
                                        name="code"
                                        type="text"
                                        required
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                        placeholder="123456"
                                        maxLength={6}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                                    />
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setIs2FARequired(false)}
                                    className="flex-1 flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Назад
                                </button>
                                <button
                                    type="submit"
                                    disabled={isVerifying}
                                    className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {isVerifying ? "Перевірка..." : "Підтвердити"}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* <div className="mt-6">
                        <div className="relative">
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                    {t("auth.no_account")}{" "}
                                    <Link
                                        to="/signup"
                                        className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
                                    >
                                        {t("auth.sign_up")}
                                    </Link>
                                </span>
                            </div>
                        </div>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default SignInPage;
